import { useEffect, useRef, useState } from 'react';

/**
 * Shared, mock-only interaction pieces for the direction-D RCM settings pages
 * (S2c interactive edit flows). No backend: dialogs edit local component state
 * and close. Chrome follows the Dialog in 00-ds-04-components.md and the
 * `setd-*` classes in settings-d.css.
 */

/** A centered modal. Esc and a backdrop click close it; focus starts inside. */
export function Dialog({
  title,
  help,
  children,
  actions,
  error,
  onClose,
}: {
  title: string;
  help?: React.ReactNode;
  children?: React.ReactNode;
  actions: React.ReactNode;
  error?: string;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('input, select, button')?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  return (
    <div
      className="setd-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div ref={ref} className="setd-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>
        {help ? <p className="setd-dialog-help">{help}</p> : null}
        {children}
        {error ? <div className="setd-dialog-err">{error}</div> : null}
        <div className="setd-dialog-actions">{actions}</div>
      </div>
    </div>
  );
}

/** Labelled text input used inside dialogs and inline edits. */
export function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  mono,
  autoComplete,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  mono?: boolean;
  autoComplete?: string;
  inputMode?: 'tel' | 'email' | 'text';
}) {
  return (
    <label className="setd-field">
      <span className="setd-field-label">{label}</span>
      <input
        className={`setd-input${mono ? ' mono' : ''}`}
        value={value}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </label>
  );
}

/** Format progressively to (XXX) XXX-XXXX while typing. */
export function formatPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return `(${d}`;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/** A field whose value is kept in the (XXX) XXX-XXXX shape as the user types. */
export function PhoneField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <TextField
      label={label}
      value={value}
      onChange={(v) => {
        onChange(formatPhone(v));
      }}
      placeholder="(210) 555-0100"
      type="tel"
      mono
      autoComplete="tel"
      inputMode="tel"
    />
  );
}

/** Minimal toast stack for the few flows that confirm with a line. */
export function useToasts() {
  const [toasts, setToasts] = useState<{ id: number; msg: string }[]>([]);
  const seq = useRef(0);
  const push = (msg: string) => {
    const id = ++seq.current;
    setToasts((t) => [...t, { id, msg }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 5000);
  };
  return { toasts, push };
}

export function Toasts({ toasts }: { toasts: { id: number; msg: string }[] }) {
  if (toasts.length === 0) return null;
  return (
    <div className="setd-toasts">
      {toasts.map((t) => (
        <div key={t.id} className="setd-toast" role="status">
          {t.msg}
        </div>
      ))}
    </div>
  );
}
